// Phase 34c.3 — distribution history sparkline.
//
// Surfaces cumulative gross distributed over time on the Partnership
// Detail page. Toggle: "All time" / "Last 30 days". 0-distribution case
// renders a friendly placeholder instead of an empty chart.
//
// Below the line: 3 micro-stats — Total distributed, # of runs,
// Avg per distribution.
//
// Recharts Area component for a soft indigo gradient fill; line on top
// stays full-opacity so the trajectory reads cleanly.

import { useMemo, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";

function fmtMoney(n) {
  return (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function fmtShortMoney(n) {
  if (!n) return "$0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return fmtMoney(n);
}

function TooltipBody({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-slate-900">
        {new Date(d.date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
      </div>
      <div className="mt-1 text-slate-600">
        Cumulative: <span className="font-medium text-slate-900 tabular-nums">{fmtMoney(d.cumulative)}</span>
      </div>
      <div className="mt-0.5 text-slate-600">
        This run: <span className="font-medium text-slate-900 tabular-nums">{fmtMoney(d.gross)}</span>
      </div>
    </div>
  );
}

export default function DistributionSparkline({ runs = [] }) {
  const [windowMode, setWindowMode] = useState("all"); // "all" | "30d"

  const finalized = useMemo(
    () => (runs || []).filter((r) => r.status === "finalized" && r.computed_at).sort(
      (a, b) => new Date(a.computed_at) - new Date(b.computed_at),
    ),
    [runs],
  );

  const filtered = useMemo(() => {
    if (windowMode === "all") return finalized;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return finalized.filter((r) => new Date(r.computed_at).getTime() >= cutoff);
  }, [finalized, windowMode]);

  const { series, totals } = useMemo(() => {
    let acc = 0;
    const s = filtered.map((r) => {
      acc += Number(r.gross_amount || 0);
      return {
        date: r.computed_at,
        gross: Number(r.gross_amount || 0),
        cumulative: acc,
      };
    });
    const totalDistributed = s.length ? s[s.length - 1].cumulative : 0;
    const avg = s.length ? totalDistributed / s.length : 0;
    return { series: s, totals: { totalDistributed, count: s.length, avg } };
  }, [filtered]);

  return (
    <div
      data-testid="partnership-distribution-sparkline"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Distribution history
          </div>
          <div className="mt-0.5 text-sm font-medium text-slate-700">
            Cumulative gross distributed over time
          </div>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs">
          {[
            { id: "all", label: "All time" },
            { id: "30d", label: "Last 30 days" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              data-testid={`partnership-distribution-sparkline-toggle-${opt.id}`}
              onClick={() => setWindowMode(opt.id)}
              className={`rounded-full px-3 py-1 font-medium transition-colors ${
                windowMode === opt.id
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 h-32 sm:h-40">
        {series.length === 0 ? (
          <div className="grid h-full place-items-center rounded-xl bg-slate-50 text-xs text-slate-500">
            No distributions in this window yet
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <defs>
                <linearGradient id="distFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#4F46E5" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis hide domain={[0, "auto"]} />
              <Tooltip content={<TooltipBody />} cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke="#4F46E5"
                strokeWidth={2}
                fill="url(#distFill)"
                isAnimationActive
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Micro-stats row */}
      <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
        <div data-testid="partnership-distribution-sparkline-stat-total">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total distributed</dt>
          <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{fmtShortMoney(totals.totalDistributed)}</dd>
        </div>
        <div data-testid="partnership-distribution-sparkline-stat-count">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total runs</dt>
          <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{totals.count}</dd>
        </div>
        <div data-testid="partnership-distribution-sparkline-stat-avg">
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Avg / distribution</dt>
          <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{fmtShortMoney(totals.avg)}</dd>
        </div>
      </dl>
    </div>
  );
}
